import { AppType, APP_TYPE } from "../app.type";
import { Type } from "../../../../shared/components";
import {
  getIdsByType,
  deleteHistoryByDocType,
  deleteBulkByType,
  importDocsByType,
  jsonImport
} from "../api.service";
import { ImportServiceType } from "./import.service.type";
import { Inject, Injectable } from "@angular/core";
import { Customer } from "../../../../shared/types/customers";
import { ProductCategory } from "../../../../shared/models/productCategory";
import { createChunk } from "../docs.util";
import { ImportError } from "../../../../shared/models/importError";

export class ErrorColumn {
  fieldId = "";
  columnIndex = "";
  posibleValue: string[] = [];
}

@Injectable()
export class ImportService implements ImportServiceType {
  type: Type | any = null;
  step = "";
  docs: any[] = [];
  props: {
    key: string;
    label: string | undefined;
  }[] = [];
  fieldList: string[] = [];
  importedKeys = new Set<string>();
  invalidHeaderColumns: Set<{ key: string; possibleField: string[] }> =
    new Set();
  stepper: "selectType" | "uploadfile" | "reviewList" = "selectType";
  invalidKeys: ErrorColumn[] = [];
  duplicatedItems: string[] = [];
  selected = new Set<string>();
  deleteStarted = false;
  invalidValues: string[] = [];
  hasInvalidValues = false;
  file: any;

  constructor(@Inject(APP_TYPE) private app: AppType) {}

  get buffer() {
    return this.app.state.importBuffer;
  }

  async upload(files: any) {
    this.step = "second";
    this.app.import.invalidKeys = [];
    this.app.spinner.showSpinner();
    if (files[0].name.split(".").pop() !== "json") {
      await this.app.importXlsx(files);
      await this.app.import.prepareDocs();
    } else {
      this.file = files[0];
    }
    if (this.app.import.invalidKeys.length > 0) {
      this.app.import.step = "error";
      this.step = "error";
    } else {
      this.app.import.step = "third";
      this.step = "third";
    }
    this.app.spinner.hideSpinner();
  }

  async prepareDocs() {
    const docs: any[] = [];
    const rows = this.app.import.buffer;
    const docsAfterImport: any[] = [];
    this.importedKeys.clear();
    this.props = this.app.field.getFieldListBasedOnType();
    Object.keys(rows).forEach((row) => {
      Object.keys(rows[row]).forEach((sheet) => {
        Object.keys(rows[row][sheet]).forEach((item) => {
          if (rows[row][sheet][item].length >= 1) {
            docsAfterImport.push(rows[row][sheet][item]);
          }
        });
      });
    });
    if (docsAfterImport.length > 1) {
      const [headerRow, ...docRows] = docsAfterImport;
      const cleanHeaderRow = Array.from(this.cleanHeaderRow(headerRow));
      if (this.invalidKeys.length > 0) {
        this.app.import.step = "error";
      } else {
        for (const docRow of docRows) {
          // create doc
          const doc: any = {};
          for (let i = 0; i < cleanHeaderRow.length; i++) {
            const key = cleanHeaderRow[i];
            let raw = "";
            if (docRow[i] !== undefined) {
              raw = docRow[i].toString();
              if (
                key === "Customer Part Number (CPN)" ||
                key === "RAD Item No."
              ) {
                raw = raw.trim();
              }
              if (raw == null) {
                continue;
              }
              try {
                const val = raw;
                doc[key] = val;
              } catch (err) {}
            }
          }
          // save doc with id
          // const rawId = docRow[0];
          const rawId = this.setIndex(docRow, cleanHeaderRow);

          try {
            const id = rawId.toString();
            doc["id"] = id.trim();
            docs.push(doc);
          } catch (err) {}
        }
        this.docs = docs;
        await this.prepareDocsToSave();
        return docs;
      }
    }
  }

  async prepareDocsToSave() {
    switch (this.type) {
      case "part":
        await this.app.part.generateParts(this.docs);
        return;
      case "assembly":
        const result = await this.app.part.generateAssembliesFromParts(
          this.docs,
        );
        this.importedKeys.clear();
        this.importedKeys.add("partNumber");
        this.importedKeys.add("parts");
        this.app.import.step = "third";
        return result;
      case "manufacturer":
        const docs = await this.app.manufacturer.generateManufacturers(
          this.docs,
        );
        this.stepper = "reviewList";
        this.app.import.step = "third";
        return docs;
      case "thread":
        return this.app.thread.generateThreads(this.docs);
      case "dinCodeResponsible":
        return this.app.dinCodeResponsible.generateResponsibles(this.docs);
      case "commodityGroupResponsible":
        return this.app.commodityGroupResponsible.generateCommodityGroupResponsibles(
          this.docs,
        );
      case "internalItem":
        return this.app.internalItems.generateInternalItems(this.docs);
      case "impact":
        if (this.app.customers.expectCurrent === Customer.DB) {
          return this.app.impacts.generateImpactsFromVehicles(this.docs);
        } else {
          return this.app.impacts.generateImpacts(this.docs);
        }
      case "vehicleResponsible":
        await this.app.users.getUsersWithRoles();
        return this.app.vehicleResponsible.generateVehicles(this.docs);
      case "productCategory":
        // remove id property
        // not needed for the structure of this type of doc
        this.docs.forEach((doc) => {
          if (doc.id) {
            delete doc.id;
          }
        });
        this.app.state.importBuffer.docs.data = this.docs;
        return this.app.state.importBuffer.docs.data;
      case "manufacturerCode":
        return this.app.manufacturerCode.generateManufacturerNames(this.docs);
      case "warehouseGroup":
        return this.app.warehouseGroup.generateWarehouseGroup(this.docs);
    }
  }

  checkForInvalidValues(docs: any) {
    if (docs.some((doc: any) => doc.invalidValue)) {
      this.hasInvalidValues = true;
    } else {
      this.hasInvalidValues = false;
    }
  }

  async saveAllDocsBasedOnType(data?: any[]) {
    if (data != null) {
      // only for json import
      this.docs = data;
    } else {
      this.app.import.docs = this.docs.filter((d) =>
        this.selected.has(d[this.getIndex()]),
      );
    }
    this.app.spinner.showSpinner();
    switch (this.app.import.type) {
      case "all-config":
        try {
          sessionStorage.setItem("fileUpload", "true");
          let response = await jsonImport(
            this.app.customers.expectCurrent,
            this.file,
          );
          if (!response.result) {
            let res = response as ImportError;
            this.app.state.next({
              hasError: true,
              errorText: this.getImportErrorText(res)
            });
            this.app.spinner.hideSpinner();
            this.step = "first";
            return;
          }
        } finally {
          sessionStorage.removeItem("fileUpload");
        }
        break;
      case "vehicleResponsible":
        this.docs = this.app.vehicleResponsible.docsToImport;
        await this.saveDocsWithChunks(this.app.import.type);
        break;
      case "part":
        await this.saveDocsWithChunks(this.app.import.type);
        break;
      case "assembly":
        this.docs.forEach((doc) => {
          doc.type = "part";
          if (doc.bom) {
            delete doc.bom;
          }
        });
        await this.saveDocsWithChunks("part");
        break;
      case "productCategory":
        const docToSave: ProductCategory = {
          type: "productCategory",
          categories: this.docs
        };
        this.app.productCategory.saveProductCategoryDoc(docToSave);
        break;
      default:
        if (this.docs) {
          await this.saveDocsWithChunks(this.app.import.type);
        }
        break;
    }

    this.app.state.next({
      hasSuccess: true,
      successText: this.app.text.import.documentsSavedSuccesfully
    });
    setTimeout(() => {
      this.app.state.next({ hasSuccess: false });
    }, 1000);
    this.app.spinner.hideSpinner();
    if (this.app.import.type === "assembly") {
      this.step = "third";
      this.app.import.step = "third";
    } else {
      this.step = "first";
      this.stepper = "selectType";
    }
    this.app.import.docs = [];
    this.app.import.importedKeys.clear();
    this.app.import.type = null;
    this.importedKeys.clear();
  }

  async saveDocsWithChunks(type: any) {
    let chunkNumber = 1;
    let isDelete = false;
    let createHistory = false;
    if (this.docs.length > 0 && this.docs[0]._deleted) {
      isDelete = true;
    }
    while (this.docs.length > 0) {
      const obj = createChunk(this.docs, this.app.import.type);
      const chunk = obj?.chunk;
      this.docs = obj?.newDocs;

      if (chunk == null) {
        return;
      }
      if (isDelete) {
        await deleteBulkByType(this.app.customers.expectCurrent, type, chunk);
      }
      if (!isDelete) {
        if (type === "part" || type === "manufacturer") {
          createHistory = true;
        }
      }
      await importDocsByType(
        this.app.customers.expectCurrent,
        type,
        createHistory,
        chunk,
      );
      chunkNumber++;
    }
    this.app.hasError = false;

    // Special conditions for the RHEINMETALLAIRDEFENCE && RHEINMETALLITALY customer
    if (
      this.app.customers.expectCurrent === Customer.RHEINMETALLAIRDEFENCE ||
      this.app.customers.expectCurrent === Customer.RHEINMETALLITALY ||
      this.app.customers.expectCurrent === Customer.RWM
    ) {
      if (type === "warehouseGroup" || type === "part") {
        await this.app.operations.triggerWork("warehouse-parts-update");
      }
    }

    // generate tree only if were imported parts or manufacturers
    if (type === "part" || type === "manufacturer") {
      await this.app.operations.triggerWork("build-bom");
      await this.app.tree.generateTree();
    }
  }

  async deleteDocs(type: Type | "all-config") {
    this.app.spinner.showSpinner();
    if (type === "all-config") {
      const types: Type[] = ["part", "manufacturer", "alert", "message"];

      for (const t of types) {
        await this.deleteAllDocsBasedOnType(t);
      }
    } else {
      await this.deleteAllDocsBasedOnType(type);
    }
    this.app.spinner.hideSpinner();
    this.app.state.next({
      hasSuccess: true,
      successText: this.app.text.import.documentsDeletedSuccesfully
    });
    setTimeout(() => {
      this.app.state.next({ hasSuccess: false });
    }, 1000);
  }

  async deleteAllDocsBasedOnType(type: Type) {
    try {
      this.deleteStarted = true;
      const alldocs = await this.getDocsToBeDeleted(type);
      if (alldocs.length === 0) {
        return;
      }
      const docsToBeDeleted: any[] = [];
      alldocs.forEach((doc: { id: string; value: string }) => {
        const newDoc = {
          _id: doc.id,
          _rev: doc.value,
          _deleted: true
        };
        docsToBeDeleted.push(newDoc);
      });
      this.docs = docsToBeDeleted;
      await this.saveDocsWithChunks(type);
      await this.deleteHistory(type);
    } catch (err) {
      console.log(err);
    }
  }

  private async deleteHistory(type: string) {
    await deleteHistoryByDocType(this.app.customers.expectCurrent, type);
  }

  async getDocsToBeDeleted(type: Type) {
    const docs = await getIdsByType(this.app.customers.expectCurrent, type);
    let allDocs: any[] = [];

    /** if type = thread, delete all the posts and impacts  */
    if (type === "thread") {
      const posts = await getIdsByType(
        this.app.customers.expectCurrent,
        "post",
      );
      const impacts = await getIdsByType(
        this.app.customers.expectCurrent,
        "impact",
      );
      docs.forEach((thread) => {
        allDocs.push(thread);
      });
      posts.forEach((post) => {
        allDocs.push(post);
      });
      impacts.forEach((impact) => {
        allDocs.push(impact);
      });
    } else {
      allDocs = docs;
    }
    return allDocs;
  }

  setIndex(docRow: string[], cleanHeaderRow: string[]) {
    switch (this.app.import.type) {
      case "assembly":
      case "part":
        return cleanHeaderRow.findIndex((key) => key === "partNumber");
      case "thread":
        return cleanHeaderRow.findIndex((key) => key === "omfNumber");
      case "manufacturer":
        return cleanHeaderRow.findIndex(
          (key) => key === "manufacturerPartNumberRaw",
        );
      case "commodityGroupResponsible":
        return cleanHeaderRow.findIndex(
          (key) => key === "commodityGroupGlobalId",
        );
      case "vehicleResponsile":
        return cleanHeaderRow.findIndex((key) => key === "vehicleName");
      case "impact":
        return cleanHeaderRow.findIndex((key) => key === "omfVehicleName");
      default:
        return docRow[0];
    }
  }

  getIndex() {
    switch (this.app.import.type) {
      case "assembly":
      case "part":
        return "partNumber";
      case "thread":
        return "omfNumber";
      case "manufacturer":
        return "manufacturerPartNumberRaw";
      case "commodityGroupResponsible":
        return "commodityGroupGlobalId";
      case "dinCode":
        return "dinCode";
      case "vehicleResponsible":
        return "vehicleName";
      case "impact":
        return "omfVehicleName";
      case "manufacturerCode":
        return "manufacturerName";
      case "warehouseGroup":
        return "warehouseGroupId";
      default:
        return "";
    }
  }

  cleanHeaderRow(rows: any[]) {
    this.importedKeys.clear();
    this.props = this.app.field.getFieldListBasedOnType();
    if (
      (this.app.import.type === "vehicleResponsible" ||
        this.app.import.type === "impact") &&
      this.app.customers.expectCurrent === Customer.DB
    ) {
      this.props = this.setProperties(this.props);
    }
    rows.forEach((key, i) => {
      let cleanKey = "";

      /** added exception for vehicle name because the label for the vehicle name also includes '-' => Vehicle-Name */
      // ["Vehicle", "Name\r\n", "omfVehicleName", ""];
      // ["Fahrzeug\r\n", "omfVehicleName", ""];
      const splitKey = key.split("-");
      if (
        splitKey[2] === "omfVehicleName" ||
        splitKey[2] === "entryCheckbox" ||
        splitKey[2] === "omApproach"
      ) {
        cleanKey = splitKey[2];
      } else {
        cleanKey = splitKey[1];
      }

      this.props.forEach((prop) => {
        if (prop.label === undefined) {
          return;
        }
        if (prop.key === cleanKey) {
          this.importedKeys.add(cleanKey);
        }
      });
      if (this.props.findIndex((field) => field.key === cleanKey) === -1) {
        const newError = new ErrorColumn();
        newError.columnIndex = (i + 1).toString();
        newError.fieldId = cleanKey;

        const possibleKeys = this.props
          .filter((field) => cleanKey.includes(field.key))
          .map((field) => field.key);
        possibleKeys.forEach((k) => {
          newError.posibleValue.push(k);
        });
        const index = this.invalidKeys.findIndex(
          (error: ErrorColumn) => error.fieldId === cleanKey,
        );
        if (index !== -1) {
          return;
        } else {
          this.invalidKeys.push(newError);
        }
      }
    });
    if (this.app.import.type === "thread") {
      // delete one of the cells because artNumber = partNumber and they are exactly the same
      this.importedKeys.delete("partNumber");
    }
    return this.importedKeys;
  }

  async checkIfExists(data: any, doc: any) {
    const dataDoc = data.find((d: any) => d._id === doc._id);
    return dataDoc;
  }

  markAsExisting(doc: any) {
    if (doc.type === "manufacturer") {
      if (
        this.app.manufacturer.existingManufacturers.findIndex(
          (manufacturer) =>
            manufacturer.nameRaw === doc.nameRaw &&
            manufacturer.manufacturerPartNumberRaw ===
              doc.manufacturerPartNumberRaw &&
            manufacturer.partNumber === doc.partNumber,
        ) !== -1
      ) {
        return true;
      }
    }
    if (doc.type === "vehicleResponsible") {
      if (
        this.app.vehicleResponsible.existingVNames.findIndex(
          (vehicleName) => vehicleName === doc.vehicleName,
        ) !== -1
      ) {
        return true;
      }
    }
    return false;
  }

  toggleDoc(doc: any, action: string) {
    // Used for Assembly -> to verify if the parts object, contains any inavlid partId
    if (doc.bom != null) {
      doc.bom.forEach((partId: string) => {
        if (this.app.part.checkPartNumberCharacters(partId)) {
          this.handleInvalidPartNumbers(
            partId,
            action === "add",
            doc["partNumber"],
          );
        }
      });
    }

    const index = doc[this.getIndex()];
    switch (action) {
      case "remove":
        this.selected.delete(index);
        this.handleInvalidPartNumbers(index, false, doc["partNumber"]);
        break;
      case "add":
        this.selected.add(index);
        this.handleInvalidPartNumbers(index, true, doc["partNumber"]);
        break;
    }
  }

  handleInvalidPartNumbers(
    index: string,
    isAdding: boolean,
    partNumber: string,
  ) {
    if (this.app.import.type === "manufacturer") {
      index = partNumber;
    }

    if (isAdding) {
      this.app.import.invalidValues.push(index);
    } else {
      const indexToRemove = index;
      const indexPosition =
        this.app.import.invalidValues.indexOf(indexToRemove);
      if (indexPosition !== -1) {
        this.app.import.invalidValues.splice(indexPosition, 1);
      }
    }

    this.app.import.hasInvalidValues = this.app.import.invalidValues.length > 0;
  }

  checkIfSelected(doc: any) {
    const index = this.getIndex();
    const result = this.selected.has((doc as Type)[index as any]);
    return result;
  }

  setProperties(props: any[]) {
    const vehicleProps = this.app.field.getFieldListBasedOnType("responsibles");
    vehicleProps.forEach((prop) => {
      props.push(prop);
    });
    return props;
  }

  getImportErrorText(error: ImportError): string {
    const errorCode = Number(error.errorCodeType);
    switch (errorCode) {
      case 0:
        return this.app.text.import[0];
      case 1:
        return this.app.text.import[1];
      case 2:
        const start = error.message.indexOf(":");
        const end = error.message.indexOf(")");

        const invalidKeys = error.message.slice(start + 1, end).trim();
        return this.app.text.import[2].replace("$", invalidKeys);
      case 3:
        return this.app.text.import[3];
      case 4:
        return this.app.text.import[4];
      case 5:
        return this.app.text.import[5];
      case 6:
        return this.app.text.import[6];
      case 7:
        let ids = error.message.split(":")[1].replace(")", "");
        if (error.message.includes("(Duplicated parts:")) {
          return this.app.text.import[7].concat(
            this.app.text.import.duplicatedParts.replace("$", ids),
          );
        }
        if (error.message.includes("(Duplicated manufacturers:")) {
          return this.app.text.import[7].concat(
            this.app.text.import.duplicatedManufacturers.replace("$", ids),
          );
        }
      default:
        return this.app.text.import[0];
    }
  }
}
